import { signIn, confirmSignIn, fetchAuthSession } from 'aws-amplify/auth';

/**
 * Employee-pool sign-in flow (pool-per-actor cutover 2026-07-04).
 *
 * The employee pool is admin-create-only with MFA REQUIRED (TOTP), so a
 * first sign-in walks: temp password → NEW_PASSWORD_REQUIRED →
 * CONTINUE_SIGN_IN_WITH_TOTP_SETUP → done. Returning sign-ins challenge
 * with CONFIRM_SIGN_IN_WITH_TOTP_CODE every time. This module maps
 * Amplify's nextStep union onto the four UI stages the Login route
 * renders (per the approved Figma frames 16/16b/16c).
 */

export type LoginStage =
  | { kind: 'credentials' }
  | { kind: 'newPassword' }
  | { kind: 'totpSetup'; setupUri: string; secret: string }
  | { kind: 'totpCode' }
  | { kind: 'done' };

/** Thrown for flow states this portal doesn't support (SMS MFA, etc.). */
export class UnsupportedStepError extends Error {
  constructor(step: string) {
    super(`Unsupported sign-in step: ${step}`);
    this.name = 'UnsupportedStepError';
  }
}

interface AmplifyNextStep {
  signInStep: string;
  totpSetupDetails?: {
    sharedSecret: string;
    getSetupUri: (appName: string, accountName?: string) => URL;
  };
}

function toStage(nextStep: AmplifyNextStep, email: string): LoginStage {
  switch (nextStep.signInStep) {
    case 'DONE':
      return { kind: 'done' };
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { kind: 'newPassword' };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
      const details = nextStep.totpSetupDetails;
      if (!details) throw new UnsupportedStepError('TOTP_SETUP without setup details');
      return {
        kind: 'totpSetup',
        setupUri: details.getSetupUri('GL Plus Admin', email).toString(),
        secret: details.sharedSecret,
      };
    }
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'totpCode' };
    default:
      throw new UnsupportedStepError(nextStep.signInStep);
  }
}

/** Start a sign-in with email + password. */
export async function startSignIn(email: string, password: string): Promise<LoginStage> {
  try {
    const out = await signIn({ username: email, password });
    return toStage(out.nextStep as AmplifyNextStep, email);
  } catch (err) {
    // A live session from a previous visit — treat as signed in.
    if ((err as { name?: string })?.name === 'UserAlreadyAuthenticatedException') {
      return { kind: 'done' };
    }
    throw err;
  }
}

/** Answer the current challenge (new password, TOTP setup code, or TOTP code). */
export async function answerChallenge(response: string, email: string): Promise<LoginStage> {
  const out = await confirmSignIn({ challengeResponse: response });
  return toStage(out.nextStep as AmplifyNextStep, email);
}

/** True if a valid session already exists (skip the form entirely). */
export async function hasSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    return Boolean(session.tokens?.idToken);
  } catch {
    return false;
  }
}

/**
 * Validate the ?next= return path: same-origin relative paths only.
 * Anything absolute, protocol-relative, or empty falls back to "/".
 */
export function safeNextPath(raw: string | null): string {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return '/';
  return raw;
}
